import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import { VenueMarker } from "./VenueMarker";
import { groupByVenue } from "@/lib/series";
import { config } from "../../lib/config";
import type { Doc } from "convex/_generated/dataModel";

// Centered on central MA — shows all of MA, CT, RI and edges of VT/NH at zoom 8
const DEFAULT_CENTER = { lat: 42.15, lng: -71.8 };
const DEFAULT_ZOOM = 8;

// Restrict panning/zoom to New England + edge of NY + bit of Maine
const NE_BOUNDS = {
  north: 47.5,
  south: 40.4,
  east: -66.5,
  west: -74.5,
};

export interface EventMapProps {
  events: Doc<"events">[];
  onEventSelect?: (event: Doc<"events">) => void;
  height?: string;
  className?: string;
}

function MapContent({
  events,
  onEventSelect,
}: {
  events: Doc<"events">[];
  onEventSelect?: (event: Doc<"events">) => void;
}) {
  const [selectedVenueKey, setSelectedVenueKey] = useState<string | null>(null);
  const [, navigate] = useLocation();

  // One pin per venue rather than one per occurrence — see VenueMarker.
  const venues = useMemo(() => groupByVenue(events), [events]);

  return (
    <>
      {venues.map((group) => (
        <VenueMarker
          key={group.key}
          group={group}
          isSelected={selectedVenueKey === group.key}
          onSelect={() => setSelectedVenueKey(group.key)}
          onClose={() => setSelectedVenueKey(null)}
          onViewEvent={(event) => {
            setSelectedVenueKey(null);
            onEventSelect?.(event);
          }}
          onViewVenue={(venueId) => {
            setSelectedVenueKey(null);
            navigate(`/venue/${venueId}`);
          }}
        />
      ))}
    </>
  );
}

export function EventMap({
  events,
  onEventSelect,
  height = "500px",
  className = "",
}: EventMapProps) {
  if (!config.googleMapsApiKey) {
    return (
      <div
        className={`flex items-center justify-center bg-secondary/30 rounded-lg ${className}`}
        style={{ height }}
      >
        <p className="text-muted-foreground text-sm">
          Map unavailable (API key not configured)
        </p>
      </div>
    );
  }

  return (
    <div className={`w-full rounded-lg overflow-hidden ${className}`} style={{ height }}>
      <APIProvider apiKey={config.googleMapsApiKey}>
        <Map
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={DEFAULT_ZOOM}
          gestureHandling="greedy"
          disableDefaultUI={false}
          zoomControl
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl
          minZoom={7}
          restriction={{ latLngBounds: NE_BOUNDS, strictBounds: false }}
          className="w-full h-full"
        >
          <MapContent
            events={events}
            onEventSelect={onEventSelect}
          />
        </Map>
      </APIProvider>
    </div>
  );
}
